/**
 * Layout.tsx — authenticated app shell. A top toolbar with session-type tabs
 * (Access / Support / Sessions) and an admin/user menu, in the style of a
 * remote-support host console; content renders below via <Outlet/>.
 */

import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useApi } from '@/api/provider';
import { useAuthStore } from '@/auth/store';

const tabs: { to: string; label: string; icon: ReactNode }[] = [
  { to: '/devices', label: 'Access', icon: <IconMonitor /> },
  { to: '/support-codes', label: 'Support', icon: <IconLifebuoy /> },
  { to: '/sessions', label: 'Sessions', icon: <IconBroadcast /> },
];

const adminLinks: { to: string; label: string; icon: ReactNode }[] = [
  { to: '/admin/orgs', label: 'Organizations', icon: <IconBuilding /> },
  { to: '/admin/users', label: 'Users', icon: <IconUsers /> },
  { to: '/admin/devices', label: 'Manage devices', icon: <IconSliders /> },
  { to: '/audit', label: 'Audit log', icon: <IconShield /> },
];

export function Layout() {
  const api = useApi();
  const claims = useAuthStore((s) => s.claims);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const role = claims?.role ?? 'technician';

  async function signOut() {
    setMenuOpen(false);
    try {
      await api.logout();
    } finally {
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-helm-bg text-helm-text">
      <header className="sticky top-0 z-20 flex h-14 items-center border-b border-helm-border bg-helm-panel px-3 sm:px-4">
        {/* Brand */}
        <div className="mr-5 flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-helm-accent to-helm-accentDim shadow-glow">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="8.5" />
              <circle cx="12" cy="12" r="2.3" fill="white" stroke="none" />
              <path d="M12 3.5v4 M12 16.5v4 M3.5 12h4 M16.5 12h4 M5.9 5.9l2.8 2.8 M15.3 15.3l2.8 2.8 M18.1 5.9l-2.8 2.8 M8.7 15.3l-2.8 2.8" />
            </svg>
          </div>
          <span className="hidden text-sm font-bold sm:block">Helm</span>
        </div>

        {/* Session-type tabs */}
        <nav className="flex items-center gap-1">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  isActive
                    ? 'bg-helm-accent/15 text-helm-text'
                    : 'text-helm-muted hover:bg-helm-elevated hover:text-helm-text'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={isActive ? 'text-helm-accent' : ''}>{t.icon}</span>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User / admin menu */}
        <div className="relative ml-auto">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-helm-elevated"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-helm-elevated text-xs font-bold uppercase text-helm-text">
              {role.charAt(0)}
            </span>
            <span className="hidden text-left leading-tight sm:block">
              <span className="block text-sm font-medium capitalize">{role.replace('_', ' ')}</span>
            </span>
            <IconChevron className={`transition ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />
              <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-helm-border bg-helm-panel py-1.5 shadow-soft">
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-helm-muted/70">Administration</div>
                {adminLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm transition ${
                        isActive ? 'bg-helm-accent/15 text-helm-text' : 'text-helm-muted hover:bg-helm-elevated hover:text-helm-text'
                      }`
                    }
                  >
                    <span className="text-helm-muted">{l.icon}</span>
                    {l.label}
                  </NavLink>
                ))}
                <div className="my-1.5 h-px bg-helm-border" />
                <button
                  type="button"
                  onClick={signOut}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm text-helm-muted transition hover:bg-helm-elevated hover:text-helm-text"
                >
                  <IconLogout />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

/* --- inline stroke icons (currentColor) --- */
function svg(children: ReactNode, size = 18) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
function IconMonitor() {
  return svg(<><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>);
}
function IconLifebuoy() {
  return svg(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" /><path d="m5.6 5.6 3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" /></>);
}
function IconBroadcast() {
  return svg(<><circle cx="12" cy="12" r="2" /><path d="M4.9 4.9a10 10 0 0 0 0 14.2M19.1 4.9a10 10 0 0 1 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4" /></>);
}
function IconBuilding() {
  return svg(<><rect x="4" y="3" width="16" height="18" rx="1.5" /><path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01M10 21v-3h4v3" /></>, 16);
}
function IconUsers() {
  return svg(<><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0M16 4.2a3.2 3.2 0 0 1 0 6.1M21 20a6 6 0 0 0-4-5.6" /></>, 16);
}
function IconSliders() {
  return svg(<><path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" /><circle cx="16" cy="6" r="2" /><circle cx="8" cy="12" r="2" /><circle cx="13" cy="18" r="2" /></>, 16);
}
function IconShield() {
  return svg(<><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" /><path d="m9 12 2 2 4-4" /></>, 16);
}
function IconLogout() {
  return svg(<><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11" /></>, 16);
}
function IconChevron({ className = '' }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-helm-muted ${className}`} aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
